import { useEffect, useState } from 'react';
import { href, useLocation } from 'react-router';
import { AppEnvironment } from '~/configuration';
import type { PluginInitContext } from '~/domain/plugins/types';
import { getLanguageCode } from '~/lib/localization/locale';
import { clientLogger } from '~/lib/logger';
import { env } from '~/platform/platform';

let initialized = false;

/**
 * Client-side initialization for cookie consent.
 *
 * The CookieFirst script is already SSR'd in <head> via the server component.
 *  * This function waits for it to finish loading (usually already done by the
 * time React hydrates), then sets up client-side behaviors.
 */
export async function init(ctx: PluginInitContext): Promise<void> {
  clientLogger.debug('[cookieconsent] init');

  if (!isCookieConsentEnabled({ preview: ctx.preview })) {
    return;
  }

  if (initialized) {
    return;
  }

  initialized = true;

  patchConsentUI(ctx.paramsLocale);
  await waitForCookieFirst(2000);
}

export function isCookieConsentEnabled({ preview }: { preview: boolean }) {
  if (AppEnvironment !== 'production') {
    return false;
  }

  if (preview) {
    return false;
  }

  if (!cookieConsentScriptUrl) {
    return false;
  }

  return true;
}

/**
 * Waits for window.CookieFirst to become available.
 *
 * Since the script tag is SSR'd in <head>, it typically finishes loading
 * before React hydrates — in that case this resolves immediately (0ms wait).
 * The timeout is a safety net for slow CDN responses.
 */
function waitForCookieFirst(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    if (window.CookieFirst) {
      resolve();
      return;
    }

    const done = () => {
      clientLogger.debug('[cookieconsent] cf_init event handler');
      clearTimeout(timeout);
      window.removeEventListener('cf_init', done);
      resolve();
    };

    window.addEventListener('cf_init', done);
    const timeout = setTimeout(done, timeoutMs);
  });
}

export const cookieConsentScriptUrl = env.COOKIEFIRST_URL;

// Facade functions
// All window.CookieFirst access is encapsulated here.
// Other modules import these instead of touching the global directly.
export function openPanel() {
  window.CookieFirst?.openPanel();
}

export function renderEmbeds() {
  window.CookieFirst?.renderEmbeds();
}

function changeLanguage(locale: string) {
  const lang = getLanguageCode(locale);
  try {
    window.CookieFirst?.changeLanguage(lang);
  } catch (error) {
    // CookieFirst throws synchronously when `lang` isn't one of the languages
    // configured for the consent banner on this domain (a fixed per-domain
    // set). Switching the banner language is a non-critical nicety, so log and
    // move on rather than let it surface as an unhandled error (US-10030).
    clientLogger.warn(
      { lang, error },
      '[cookieconsent] changeLanguage skipped'
    );
  }
}

function notifyViewChanged() {
  window.CookieFirst?.notifyViewChanged();
}

let patched = false;
export function patchConsentUI(paramsLocale: string | undefined) {
  if (patched) {
    return;
  }
  patched = true;

  patchPrivacyPolicyLink(paramsLocale);
  patchConsentUIColors();

  window.addEventListener('cf_layer_ready', (_event: Event) => {
    clientLogger.debug('[cookieconsent] cf_layer_ready event handler');
    patchPrivacyPolicyLink(paramsLocale);
    patchConsentUIColors();
  });
}

/**
 * CookieFirst hardcodes the privacy link to https://understory.io/privacy-policy.
 * This listens for the `cf_layer_ready` event and rewrites the link to the
 * storefront's locale-aware privacy page. Uses the URL's `params.locale` so
 * the rewritten link preserves the visitor's current locale segment.
 */
function patchPrivacyPolicyLink(paramsLocale: string | undefined) {
  const privacyLink = document?.querySelector(
    'a[href="https://understory.io/privacy-policy/"]'
  );
  if (privacyLink) {
    privacyLink.setAttribute(
      'href',
      href('/:locale?/terms/:type?', { locale: paramsLocale, type: 'privacy' })
    );
    privacyLink.setAttribute('target', '_self');
  }
}
function patchConsentUIColors() {
  const pandaElement =
    document.querySelector<HTMLElement>('[data-panda-theme]');
  const dialogs = document.querySelectorAll<HTMLElement>('.cookiefirst-root');

  if (pandaElement && dialogs.length > 0) {
    applyBannerTheme(getComputedStyle(pandaElement), dialogs);
  }
}

const bannerThemeProperties = [
  ['--banner-accent-color', '--colors-interactive-accent-default'],
  ['--banner-outline-color', '--colors-content-primary'],
  ['--banner-font-family', '--fonts-default'],
  ['--banner-bg-color', '--colors-surface-raised'],
  ['--banner-text-color', '--colors-content-primary'],
  ['--banner-btn-primary-text', '--colors-content-onaccent'],
  ['--banner-btn-primary-bg', '--colors-interactive-accent-default'],
  ['--banner-btn-primary-border-color', '--colors-interactive-accent-default'],
  ['--banner-btn-primary-border-radius', '--leaf-button-border-radius'],
  ['--banner-btn-primary-hover-text', '--colors-content-onaccent'],
  ['--banner-btn-primary-hover-bg', '--colors-interactive-accent-hover'],
  [
    '--banner-btn-primary-hover-border-color',
    '--colors-interactive-accent-hover',
  ],
  ['--banner-btn-primary-hover-border-radius', '--leaf-button-border-radius'],
  ['--banner-btn-secondary-text', '--colors-content-primary'],
  ['--banner-btn-secondary-bg', '--colors-interactive-secondary'],
  ['--banner-btn-secondary-border-color', '--colors-interactive-secondary'],
  ['--banner-btn-secondary-border-radius', '--leaf-button-border-radius'],
  ['--banner-btn-secondary-hover-text', '--colors-content-primary'],
  ['--banner-btn-secondary-hover-bg', '--colors-interactive-secondary'],
  [
    '--banner-btn-secondary-hover-border-color',
    '--colors-interactive-secondary',
  ],
  ['--banner-btn-secondary-hover-border-radius', '--leaf-button-border-radius'],
] as const;

/**
 * Every theme read happens before the first banner write: reading a computed
 * style after an inline-style write forces a synchronous style recalculation,
 * and interleaving them cost one per property each time the banner appeared.
 */
export function applyBannerTheme(
  theme: Pick<CSSStyleDeclaration, 'getPropertyValue'>,
  bannerRoots: Iterable<{ style: Pick<CSSStyleDeclaration, 'setProperty'> }>
) {
  const values = bannerThemeProperties.map(
    ([bannerProperty, themeProperty]) =>
      [bannerProperty, theme.getPropertyValue(themeProperty)] as const
  );

  for (const root of bannerRoots) {
    for (const [property, value] of values) {
      root.style.setProperty(property, value, 'important');
    }
  }
}

export function CookieConsentRouteHandler({ locale }: { locale: string }) {
  const location = useLocation();
  const [onPathname] = useState(() =>
    onNavigation(() => {
      notifyViewChanged();
      renderEmbeds();
    })
  );

  useEffect(() => {
    changeLanguage(locale);
  }, [locale]);

  useEffect(() => {
    onPathname(location.pathname);
  }, [location.pathname, onPathname]);

  return null;
}

/**
 * Runs `rescan` for each pathname after the first one it sees.
 *
 * CookieFirst scans the page itself when it loads, so telling it about the
 * page the visitor landed on only repeats that scan. Each scan walks the
 * whole document, about 120 ms on a typical home page at 6x CPU slowdown and
 * over a second on one with 200+ experience cards, and it ran in the task
 * right after hydration.
 */
export function onNavigation(rescan: () => void): (pathname: string) => void {
  let current: string | undefined;
  return (pathname) => {
    if (pathname === current) return;
    const isLanding = current === undefined;
    current = pathname;
    if (!isLanding) rescan();
  };
}
